import { FC, RefObject, memo, useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/shared/ui';
import { LESSON_TYPES, OHIO_COMPLIANCE } from '@/shared/constants';
import { createPortal } from 'react-dom';

import RestrictedVideoPlayer from '@/features/courses/components/RestrictedVideoPlayer/RestrictedVideoPlayer';
import RestrictedYouTubePlayer from '../RestrictedYouTubePlayer/RestrictedYouTubePlayer';
import Quiz from '@/features/courses/components/Quiz/Quiz';
import styles from './CoursePlayerContent.module.css';
import { Lesson } from '@/features/courses/api/lesson-services';
import { parseLessonMarkdown } from '../../utils/lessonContentParser';
import LessonContentRenderer from '../LessonContentRenderer/LessonContentRenderer';

interface CoursePlayerContentProps {
    currentLesson: Lesson | null;
    lessonProgress?: any;
    setSidebarOpen: (open: boolean) => void;
    // Video Props
    videoRef: RefObject<any>;
    handleVideoEnded: () => void;
    updateVideoProgress: (current: number, duration: number, percent: number) => void;
    videoDuration: number;
    setVideoDuration: (duration: number) => void;
    // Quiz Props
    quizAttemptId: string | null;
    setQuizAttemptId: (id: string | null) => void;
    handleQuizStart?: (quizId?: string) => void;
    handleQuizSubmit: (attempt: any) => Promise<any>;
    quizSubmitting: boolean;
    quizError: string | null;
    // Theatre Mode Props
    activeTheatreVideo: { url: string; isRequired: boolean } | null;
    setActiveTheatreVideo: (video: { url: string; isRequired: boolean } | null) => void;
    closeTheatreMode: () => Promise<void>;
    watchedInlineVideos: Set<string>;
    setWatchedInlineVideos: React.Dispatch<React.SetStateAction<Set<string>>>;
    theatreVideoRef: RefObject<any>;
    nextLesson: Lesson | null;
    onComplete: () => Promise<void>;
    onNavigate: (lesson: Lesson) => void;
    scrollContainerRef?: RefObject<HTMLDivElement>;
}

const CoursePlayerContent: FC<CoursePlayerContentProps> = memo(({
    currentLesson,
    lessonProgress,
    setSidebarOpen,
    videoRef,
    handleVideoEnded,
    updateVideoProgress,
    videoDuration,
    setVideoDuration,
    quizAttemptId,
    setQuizAttemptId,
    handleQuizStart,
    handleQuizSubmit,
    quizSubmitting,
    quizError,
    activeTheatreVideo,
    setActiveTheatreVideo,
    closeTheatreMode,
    watchedInlineVideos,
    setWatchedInlineVideos,
    theatreVideoRef,
    nextLesson,
    onComplete,
    onNavigate,
    scrollContainerRef,
}) => {
    const readingContentRef = useRef<HTMLDivElement>(null);

    // Event delegation for inline video cards
    useEffect(() => {
        const handleContentClick = (e: MouseEvent) => {
            const card = (e.target as HTMLElement).closest('.inline-video-card');
            if (card) {
                const url = card.getAttribute('data-video-url');
                const isRequired = card.getAttribute('data-required') === 'true';
                if (url) {
                    setActiveTheatreVideo({ url, isRequired });
                    setSidebarOpen(false); // Close sidebar during theatre mode
                }
            }
        };

        const container = readingContentRef.current;
        if (container) {
            container.addEventListener('click', handleContentClick);
            return () => container.removeEventListener('click', handleContentClick);
        }
    }, [setSidebarOpen, setActiveTheatreVideo]);

    const closeTheatre = useCallback(() => {
        setActiveTheatreVideo(null);
    }, [setActiveTheatreVideo]);

    const handleTheatreVideoEnded = useCallback(() => {
        if (activeTheatreVideo) {
            setWatchedInlineVideos(prev => new Set([...prev, activeTheatreVideo.url]));
        }
        // Save progress immediately on end
        if (activeTheatreVideo && theatreVideoRef.current) {
            const duration = theatreVideoRef.current.duration;
            updateVideoProgress(duration, duration, 100);
        }
        // Save and close
        setTimeout(closeTheatreMode, 1500);
    }, [activeTheatreVideo, closeTheatreMode, setWatchedInlineVideos, updateVideoProgress, theatreVideoRef]);

    const onPlayInternal = useCallback(() => setSidebarOpen(false), [setSidebarOpen]);
    const onPauseInternal = useCallback(() => setSidebarOpen(true), [setSidebarOpen]);

    const onTimeUpdateInternal = useCallback((progressData: any) => {
        updateVideoProgress(
            progressData.currentTime,
            progressData.duration,
            progressData.percentWatched
        );
    }, [updateVideoProgress]);

    const onLoadedMetadataInternal = useCallback((meta: any) => {
        setVideoDuration(meta.duration);
    }, [setVideoDuration]);

    const isYouTubeUrl = useCallback((url: string) => url.includes('youtube.com') || url.includes('youtu.be'), []);

    const isVideoWatched = useCallback((url: string): boolean => {
        if (watchedInlineVideos.has(url)) return true;
        const sanitizedUrl = url.replace(/[^a-zA-Z0-9]/g, '_');
        const videoProg = lessonProgress?.supplementalVideoProgress?.[sanitizedUrl];
        return !!(videoProg && videoProg.percentWatched >= 90);
    }, [watchedInlineVideos, lessonProgress]);

    const extractYouTubeVideoId = useCallback((url: string): string | null => {
        try {
            // Handle youtube.com/watch?v=VIDEO_ID format
            const watchMatch = url.match(/v=([A-Za-z0-9_-]{11})/);
            if (watchMatch) return watchMatch[1];

            // Handle youtu.be/VIDEO_ID format
            const shortMatch = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
            if (shortMatch) return shortMatch[1];

            // Handle already embedded format youtube.com/embed/VIDEO_ID
            const embedMatch = url.match(/embed\/([A-Za-z0-9_-]{11})/);
            if (embedMatch) return embedMatch[1];

            return null;
        } catch {
            return null;
        }
    }, []);

    const normalizeYouTubeEmbedUrl = useCallback((url: string): string | null => {
        const videoId = extractYouTubeVideoId(url);
        if (!videoId) return null;

        // Extract existing query parameters (except v= and list=)
        try {
            const urlObj = new URL(url);
            const params = new URLSearchParams(urlObj.search);

            // Remove standard YouTube parameters we'll handle separately
            params.delete('v');
            params.delete('list');
            params.delete('autoplay');

            // Build the embed URL with preserved parameters
            let embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
            const remainingParams = params.toString();
            if (remainingParams) {
                embedUrl += `&${remainingParams}`;
            }

            return embedUrl;
        } catch {
            // Fallback if URL parsing fails
            return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
        }
    }, [extractYouTubeVideoId]);

    const memoizedParsedContent = useMemo(() => {
        return parseLessonMarkdown(currentLesson?.content || '');
    }, [currentLesson?.id, currentLesson?.content]);

    if (!currentLesson) {
        return (
            <div className={styles.emptyState}>
                <p>Select a lesson to begin</p>
            </div>
        );
    }

    switch (currentLesson.type) {
        case LESSON_TYPES.VIDEO: {
            const videoInitialTime = lessonProgress?.videoProgress?.currentTime || 0;
            return (
                <div className={styles.videoContainer}>
                    <RestrictedVideoPlayer
                        ref={videoRef}
                        src={currentLesson.videoUrl || ''}
                        initialTime={videoInitialTime}
                        onEnded={handleVideoEnded}
                        onPlay={onPlayInternal}
                        onPause={onPauseInternal}
                        onTimeUpdate={onTimeUpdateInternal}
                        onLoadedMetadata={onLoadedMetadataInternal}
                    />
                </div>
            );
        }

        case LESSON_TYPES.READING:
            return (
                <div className={styles.readingContent} ref={readingContentRef}>
                    <LessonContentRenderer
                        content={memoizedParsedContent}
                        nextLesson={nextLesson}
                        currentLesson={currentLesson}
                        isCompleted={lessonProgress?.completed}
                        onComplete={onComplete}
                        onNavigate={onNavigate}
                        scrollContainerRef={scrollContainerRef}
                        lessonProgress={lessonProgress}
                        watchedInlineVideos={watchedInlineVideos}
                    />

                    {/* Theatre Mode Overlay Portal */}
                    {activeTheatreVideo && createPortal(
                        <div className={styles.theatreOverlay}>
                            <div className={styles.theatreContent}>
                                <button className={styles.closeTheatre} onClick={closeTheatreMode} aria-label="Close Theatre Mode">×</button>
                                {isYouTubeUrl(activeTheatreVideo.url) ? (
                                    <RestrictedYouTubePlayer
                                        ref={theatreVideoRef}
                                        src={activeTheatreVideo.url}
                                        initialTime={
                                            lessonProgress?.supplementalVideoProgress?.[activeTheatreVideo.url.replace(/[^a-zA-Z0-9]/g, '_')]?.currentTime ||
                                            0
                                        }
                                        onTimeUpdate={onTimeUpdateInternal}
                                        onEnded={handleTheatreVideoEnded}
                                        allowSeeking={!activeTheatreVideo.isRequired || isVideoWatched(activeTheatreVideo.url)}
                                        autoPlay={true}
                                    />
                                ) : (
                                    <RestrictedVideoPlayer
                                        ref={theatreVideoRef}
                                        src={activeTheatreVideo.url}
                                        initialTime={
                                            lessonProgress?.supplementalVideoProgress?.[activeTheatreVideo.url.replace(/[^a-zA-Z0-9]/g, '_')]?.currentTime ||
                                            0
                                        }
                                        onTimeUpdate={onTimeUpdateInternal}
                                        onEnded={handleTheatreVideoEnded}
                                        allowSeeking={!activeTheatreVideo.isRequired || isVideoWatched(activeTheatreVideo.url)}
                                        autoPlay={true}
                                    />
                                )}
                            </div>
                        </div>,
                        document.body
                    )}
                </div>
            );

        case LESSON_TYPES.QUIZ:
            if (!quizAttemptId) {
                return (
                    <div className={styles.quizContent}>
                        <div className={styles.quizInitial}>
                            {currentLesson.content ? (
                                <div className={styles.briefingContainer}>
                                    <LessonContentRenderer
                                        content={memoizedParsedContent}
                                        nextLesson={nextLesson}
                                        currentLesson={currentLesson}
                                        isCompleted={lessonProgress?.completed}
                                        onComplete={onComplete}
                                        onNavigate={onNavigate}
                                        scrollContainerRef={scrollContainerRef}
                                    />
                                </div>
                            ) : (
                                <h3>{currentLesson.title}</h3>
                            )}

                            {currentLesson.description && !currentLesson.content && <p>{currentLesson.description}</p>}

                            <div className={styles.quizInfo}>
                                <p className={styles.quizInfoItem}>
                                    <strong>Questions:</strong> {currentLesson.questions?.length || 0}
                                </p>
                                <p className={styles.quizInfoItem}>
                                    <strong>Passing Score:</strong> {currentLesson.passingScore || 70}%
                                </p>
                                <p className={styles.warningNote}>
                                    ⚠️ <strong>Important:</strong> Once you start the quiz, correct answers will only be shown after you submit.
                                </p>
                            </div>
                            <div className={styles.quizStartAction}>
                                <Button
                                    variant="primary"
                                    onClick={() => handleQuizStart?.()}
                                    disabled={quizSubmitting}
                                    size="large"
                                >
                                    Start Assessment
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            }
            return (
                <div className={styles.quizContent}>
                    <Quiz
                        quiz={currentLesson as any}
                        onSubmit={handleQuizSubmit}
                        loading={quizSubmitting}
                        error={quizError}
                        onCancel={() => setQuizAttemptId(null)}
                    />
                </div>
            );

        default:
            return (
                <div className={styles.defaultContent}>
                    <p>{currentLesson.description}</p>
                </div>
            );
    }
});

export default CoursePlayerContent;
